import type { IAudioAssetResponse, ISlideshowVideoJobResponse, ISlideshowVideoValidationResponse, ISlideshowVideoWarning } from "@/types"

export enum SlideshowVideoStatus {
  Queued = 0,
  Processing = 1,
  Completed = 2,
  Failed = 3,
}

export class SlideshowVideoJob {
  public id?: number
  public status?: SlideshowVideoStatus
  public createdAt?: Date
  public fileName?: string
  public downloadUrl?: string

  constructor(job: Partial<SlideshowVideoJob>) {
    this.id = job.id
    this.status = job.status
    this.createdAt = job.createdAt
    this.fileName = job.fileName
    this.downloadUrl = job.downloadUrl
  }

  public get isInProgress() {
    return this.status === SlideshowVideoStatus.Queued || this.status === SlideshowVideoStatus.Processing
  }

  public get isCompleted() {
    return this.status === SlideshowVideoStatus.Completed
  }

  public get isFailed() {
    return this.status === SlideshowVideoStatus.Failed
  }

  public static fromResponse(response?: ISlideshowVideoJobResponse) {
    return new SlideshowVideoJob({
      id: response?.id,
      status: response?.status,
      createdAt: response?.dateCreated ? new Date(response.dateCreated) : undefined,
      fileName: response?.fileName,
      downloadUrl: response?.downloadUrl,
    })
  }
}

export class SlideshowVideoValidation {
  public errorCode?: string
  public errorField?: string
  public warnings: ISlideshowVideoWarning[] = []
  public planId?: number
  public minMediaCount?: number
  public maxMediaCount?: number
  public maxDurationSeconds?: number
  public currentMediaCount?: number
  public currentDurationSeconds?: number
  public currentTitleSlideDurationSeconds?: number
  public estimatedTotalDurationSeconds?: number
  public maxTotalDurationSeconds?: number

  constructor(validation: Partial<SlideshowVideoValidation>) {
    this.errorCode = validation.errorCode
    this.errorField = validation.errorField
    this.warnings = validation.warnings ?? []
    this.planId = validation.planId
    this.minMediaCount = validation.minMediaCount
    this.maxMediaCount = validation.maxMediaCount
    this.maxDurationSeconds = validation.maxDurationSeconds
    this.currentMediaCount = validation.currentMediaCount
    this.currentDurationSeconds = validation.currentDurationSeconds
    this.currentTitleSlideDurationSeconds = validation.currentTitleSlideDurationSeconds
    this.estimatedTotalDurationSeconds = validation.estimatedTotalDurationSeconds
    this.maxTotalDurationSeconds = validation.maxTotalDurationSeconds
  }

  public get isValid() {
    return !this.errorCode
  }

  public static fromResponse(response?: ISlideshowVideoValidationResponse) {
    const extensions = response?.meta?.extensions

    return new SlideshowVideoValidation({
      errorCode: response?.errorCode,
      errorField: response?.errorField,
      warnings: response?.warnings ?? [],
      planId: extensions?.planId,
      minMediaCount: extensions?.minMediaCount,
      maxMediaCount: extensions?.maxMediaCount,
      maxDurationSeconds: extensions?.maxDurationSeconds,
      currentMediaCount: extensions?.currentMediaCount,
      currentDurationSeconds: extensions?.currentDurationSeconds,
      currentTitleSlideDurationSeconds: extensions?.currentTitleSlideDurationSeconds,
      estimatedTotalDurationSeconds: extensions?.estimatedTotalDurationSeconds,
      maxTotalDurationSeconds: extensions?.maxTotalDurationSeconds,
    })
  }
}

export class AudioAsset {
  public assetId?: number
  public title?: string
  public artists?: string
  public previewUrl?: string

  constructor(asset: Partial<AudioAsset>) {
    this.assetId = asset.assetId
    this.title = asset.title
    this.artists = asset.artists
    this.previewUrl = asset.previewUrl
  }

  public static fromResponse(response?: IAudioAssetResponse) {
    return new AudioAsset({
      assetId: response?.assetId,
      title: response?.title,
      artists: response?.artists ?? undefined,
      previewUrl: response?.previewUrl,
    })
  }
}
